<template>
  <div class="filter-form">
    <GridContainer>
      <div class="filter-form__filter-wrapper">
        <!-- Filters -->
        <section
          class="filter-form__filter-container"
          role="region"
          aria-labelledby="filter-section-label"
        >
          <span
            id="filter-section-label"
            class="filter-form__label"
          >
            Filtern nach:
          </span>

          <!-- Product Categories -->
          <ProductCategoryDropdown
            v-if="!hiddenFilters?.includes('productCategories')"
            v-model="fields.filter.productCategoryPaths"
            class="filter-form__entry"
            :aggregations="productCategoryAggregations"
            :item-url="itemUrls.productCategories"
            :disabled="isDisplexFullcoverOrPrivacy && hasRedundantFilterOptions('categories')"
            blank-text="Alle Kategorien"
            :tree="crossCategory"
            @change="submit"
          />

          <!-- Brands -->
          <ListDropdown
            v-if="!hiddenFilters?.includes('brands')"
            v-model="fields.filter.brandSlugs"
            placeholder="Marke"
            label="Marke wählen"
            multiple
            has-search
            :options="options.brands"
            :item-url="itemUrls.brands"
            class="filter-form__entry"
            include-blank="Alle Marken"
            @change="submit"
          />

          <!-- Colors -->
          <ListDropdown
            v-if="!hiddenFilters?.includes('colors')"
            v-model="fields.filter.colorSlugs"
            placeholder="Farben"
            label="Farbe wählen"
            multiple
            :options="options.colors"
            :item-url="itemUrls.colors"
            :disabled="listDropdownDisabled('colors')"
            include-blank="Alle Farben"
            class="filter-form__entry"
            @change="submit"
          >
            <template #beforeLabel="{ option }">
              <span
                class="filter-form__entry-color-preview"
                :style="{ backgroundColor: colorCodesBySlugs(option.value) }"
              />
            </template>
          </ListDropdown>

          <!-- Compatible Product Families -->
          <ListDropdown
            v-if="
              !$config.public.performanceMode.disableFilter &&
              !hiddenFilters?.includes('compatibleProductFamilies')
            "
            v-model="fields.filter.compatibleProductFamilySlugs"
            placeholder="Kompatibilität"
            label="Kompatibilität wählen"
            multiple
            :options="options.compatibleProductFamilies"
            :item-url="itemUrls.compatibleProductFamilies"
            :disabled="!options.compatibleProductFamilies.length"
            group-entries
            has-search
            include-blank="Egal"
            class="filter-form__entry"
            @change="submit"
          />

          <!-- Prices -->
          <PriceFilterDropdown
            :discounted="fields.filter.discounted || undefined"
            :discounted-count="discountedOffersCount"
            :onetime-price="fields.filter.onetimePrice || undefined"
            :disabled="priceFilterDisabled"
            :allow-discounts="!hiddenFilters?.includes('discounts')"
            class="filter-form__entry"
            @change="updatePrices"
          />
        </section>

        <!-- Sort -->
        <section
          v-if="!hideSortDropdown"
          class="filter-form__sort-container"
          role="region"
          aria-labelledby="sort-section-label"
        >
          <span
            id="sort-section-label"
            class="filter-form__label"
          >
            Sortieren nach:
          </span>
          <SortDropdown
            :model-value="variables?.sort ?? undefined"
            :disabled="!hasOffers"
            :search-context="inSearchContext"
            class="filter-form__entry-sort"
            @change="updateSort"
          />
        </section>
        <div class="filter-form__entry-wide-fit filter-form__toggle-container">
          <!-- filter provider -->
          <template v-if="!hiddenFilters?.includes('providers')">
            <Toggle
              v-for="provider in providers"
              :key="provider.slug"
              v-model="providerToggleValue[provider.slug]"
              :label="providerLabel(provider.name)"
              :disabled="providerFilterDisabled"
              @update:model-value="toggleProvider(provider.slug, $event)"
            />
          </template>

          <template v-if="showSmarthomeFilter">
            <!-- wlan filter -->
            <template v-if="!hiddenFilters?.includes('wlan') && wlanTag">
              <Toggle
                :key="wlanTag?.tag?.slug || undefined"
                v-model="isWlanSelected"
                class="filter-form__wlan-toggle"
                @update:model-value="toggleWlan($event)"
              >
                <SvgIcon
                  :image="wlanIcon"
                  class="filter-form__wlan-toggle-icon"
                />
                WLAN-Anbindung
              </Toggle>
            </template>

            <div
              v-if="
                !(hiddenFilters?.includes('mza') && hiddenFilters.includes('msh')) &&
                (mzaCompatibleTag || mshCompatibleTag)
              "
              class="filter-form__app-toggle-wrapper filter-form__entry-wide-fit"
            >
              <!-- mza filter -->
              <span class="filter-form__app-toggle-label">Kompatibel mit</span>
              <div
                v-if="!hiddenFilters?.includes('mza') && mzaCompatibleTag"
                class="filter-form__entry-wide-fit filter-form__app-toggle"
              >
                <Toggle
                  :key="mzaCompatibleTag?.tag?.slug || undefined"
                  v-model="isMzaSelected"
                  title="MagentaZuhause App"
                  @update:model-value="toggleMza($event)"
                >
                  <SvgIcon
                    :image="mzaIcon"
                    class="filter-form__app-toggle-icon"
                  />

                  MagentaZuhause App
                </Toggle>
              </div>

              <!-- msh filter -->
              <div
                v-if="!hiddenFilters?.includes('msh') && mshCompatibleTag"
                class="filter-form__entry-wide-fit filter-form__app-toggle"
              >
                <Toggle
                  :key="mshCompatibleTag?.tag?.slug || undefined"
                  v-model="isMshSelected"
                  title="Magenta SmartHome App"
                  @update:model-value="toggleMsh($event)"
                >
                  <SvgIcon
                    :image="mshIcon"
                    class="filter-form__app-toggle-icon"
                  />

                  Magenta SmartHome App
                </Toggle>
              </div>
            </div>
          </template>

          <slot name="additionalToggle" />
        </div>

        <slot name="additionalFilters" />
      </div>

      <!-- Selected Filters -->
      <ActiveFilters
        :value="activeFilters"
        :has-filter="hasUserFilter"
        class="filter-form__active"
        @remove="removeFilter"
        @clear="clearFilters"
      />
    </GridContainer>
  </div>
</template>

<script setup lang="ts">
import { isEmpty, isUndefined, without } from 'lodash-es';

import {
  GridContainer,
  Toggle,
  ListDropdown,
  type FilterOption,
  type FilterValue,
} from '@i22team/td-longtail-vue-components';
import ActiveFilters from '~/components/filter/active-filters.vue';
import mergeFilterVariables from '#shared/filter-params/merge-variables';
import { cleanFilterVariables } from '#shared/filter-params/clean-filters';
import PriceFilterDropdown from '~/components/price-filter-dropdown.vue';
import ProductCategoryDropdown from '~/components/product-category-dropdown.vue';
import SortDropdown from '~/components/sort-dropdown.vue';
import type {
  OfferColorAggregation,
  Maybe,
  OfferProductCategoryAggregation,
  ProductCategory,
  ProductProviderKey,
  OfferProductTagAggregation,
} from '#shared/types/goliath/goliath.types';

import wlanIcon from '~/assets/images/connection-wlan.svg';
import mzaIcon from '~/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '~/assets/images/msh-logo.svg';
import traverseAggregations from '~/lib/aggregation-utils/traverse';

const providers: FilterProvider[] = [
  { slug: 'TELEKOM' as ProductProviderKey.Telekom, name: 'Telekom Deutschland GmbH' },
  { slug: 'BRODOS' as ProductProviderKey.Brodos, name: 'Brodos AG' },
];
const providerLabelPrefix = 'Versand und Verkauf durch';

const FIELDS: FilterQueryFilled = {
  filter: {
    productCategoryPaths: [],
    brandSlugs: [],
    colorSlugs: [],
    compatibleProductFamilySlugs: [],
    discounted: undefined,
    onetimePrice: undefined,
    productProviders: [],
    productTagSlugs: [],
    anyInGroupOfProductTagSlugs: [],
  },
  pagination: undefined,
  sort: undefined,
  search: undefined,
};

const AGGREGATION_BUCKETS: AggregationBucket = {
  productCategories: {
    source: 'productCategory',
    key: 'productCategoryPaths',
    getLabel({ name }) {
      return name;
    },
    getValue({ path }) {
      return path;
    },
  },
  brands: {
    source: 'brand',
    key: 'brandSlugs',
    getLabel({ name }) {
      return name;
    },
    getValue({ slug }) {
      return slug;
    },
  },
  colors: {
    source: 'color',
    key: 'colorSlugs',
    getLabel({ name }) {
      return name;
    },
    getValue({ slug }) {
      return slug;
    },
  },
  compatibleProductFamilies: {
    source: 'productFamily',
    key: 'compatibleProductFamilySlugs',
    getLabel({ model }) {
      return model;
    },
    getValue({ slug }) {
      return slug;
    },
  },
  productTags: {
    source: 'tag',
    key: 'productTagSlugs',
    getLabel({ name }) {
      return name;
    },
    getValue({ slug }) {
      return slug;
    },
  },
};

// Gets option values that are available for an aggregation with a particular
// name. Note that only bucket aggregations may have option values.
function getOptionsFromBucket(
  aggregations: SelectedOfferAggregations | undefined,
  name: AggregationTypes
) {
  if (!aggregations) return [];
  const currentAggregation = aggregations[name];
  if (!currentAggregation || !Array.isArray(currentAggregation)) return [];
  const definition = AGGREGATION_BUCKETS[name];
  const options: FilterOption[] = [];
  currentAggregation.forEach((aggregation) => {
    // @ts-ignore
    const source = aggregation[definition.source];
    const label = definition.getLabel(source) || '';
    const value = definition.getValue ? definition.getValue(source) : source;
    options.push({
      groupName: source.brand?.name,
      label,
      value,
      count: aggregation.offersCount || 0,
    });
  });
  return options;
}

export type FilterFormProps = {
  variables?: FilterQuery;
  aggregations?: SelectedOfferAggregations;
  itemUrls: ItemUrls;
  crossCategory?: boolean;
  hiddenFilters?: (keyof AggregationBucket | 'mza' | 'msh' | 'discounts' | 'wlan' | 'providers')[];
  currentOffersCount?: number;
  hideSortDropdown?: boolean;
};

const props = withDefaults(defineProps<FilterFormProps>(), {
  variables: undefined,
  aggregations: undefined,
  hiddenFilters: () => [],
  currentOffersCount: 0,
  crossCategory: false,
});

const emits = defineEmits<{
  (e: 'submit', fields: FilterQueryFilled): void;
}>();

const fields = ref<FilterQueryFilled>(
  mergeFilterVariables<FilterQuery>(FIELDS, props.variables) as FilterQueryFilled
);

watch(
  () => props.variables,
  (variables) => {
    fields.value = mergeFilterVariables<FilterQuery>(FIELDS, variables) as FilterQueryFilled;
  }
);

const hasUserFilter = computed(() => {
  const { filter, search, complexSearch } = fields.value;
  if (search) return true;
  return (
    (filter.brandSlugs && filter.brandSlugs.length > 0) ||
    (filter.colorSlugs && filter.colorSlugs?.length > 0) ||
    (filter.compatibleProductFamilySlugs && filter.compatibleProductFamilySlugs?.length > 0) ||
    filter.discounted === true ||
    !!(filter.onetimePrice?.min || filter.onetimePrice?.max) ||
    (filter.productTagSlugs && filter.productTagSlugs.length > 0) ||
    (filter.anyInGroupOfProductTagSlugs && filter.anyInGroupOfProductTagSlugs.length > 0) ||
    (filter.productProviders && filter.productProviders?.length > 0) ||
    !!complexSearch
  );
});
const hasOffers = computed(() => props.currentOffersCount > 0);

const providerLabel = computed(
  () => (providerName: string) => `${providerLabelPrefix} ${providerName}`
);

const providerToggleValue = computed(() =>
  providers.reduce((acc: Record<string, boolean>, { slug }) => {
    acc[slug] = fields.value.filter.productProviders?.includes(slug) || false;
    return acc;
  }, {})
);
const options = computed<Record<AggregationTypes, FilterOption[]>>(() => ({
  productCategories: getOptionsFromBucket(props.aggregations, 'productCategories'),
  brands: getOptionsFromBucket(props.aggregations, 'brands'),
  colors: getOptionsFromBucket(props.aggregations, 'colors'),
  compatibleProductFamilies: getOptionsFromBucket(props.aggregations, 'compatibleProductFamilies'),
  productTags: getOptionsFromBucket(props.aggregations, 'productTags'),
}));

const colorCodesBySlugs = (colorSlug: FilterValue) => {
  if (!props.aggregations?.colors) return undefined;
  return props.aggregations.colors.find((entry: Maybe<OfferColorAggregation>) => {
    if (!entry?.color) return undefined;
    if (entry.color.slug === colorSlug && entry.color.code) {
      return entry.color.code;
    }
    return undefined;
  })?.color?.code;
};

const productCategoryAggregations = computed<OfferProductCategoryAggregation[]>(
  () => props.aggregations?.productCategories || []
);
const rootProductCategory = computed(() => {
  const agg = productCategoryAggregations.value.find((aggregations) => aggregations.selected);
  if (!agg) return undefined;
  return agg.productCategory as ProductCategory;
});
const selectedProductCategories = computed<ProductCategory[]>(() => {
  const aggs = traverseAggregations(productCategoryAggregations.value, {
    filter: (aggregation: OfferProductCategoryAggregation, depth: number) =>
      aggregation.selected && depth === 1,
  });
  return aggs.map((agg) => agg.productCategory);
});

const priceFilterLabel = computed(() => {
  const filter = fields.value.filter.onetimePrice;
  if (!filter || isEmpty(filter)) return undefined;
  if (filter.max === 0) {
    return 'Kostenlos';
  }
  if (!isUndefined(filter.min) && !isUndefined(filter.max)) {
    if (filter.min === filter.max) {
      return `Preis von ${filter.min} €`;
    }
    return `Preise von ${filter.min} € bis ${filter.max} €`;
  }
  if (!isUndefined(filter.min)) {
    return `Preise ab ${filter.min} €`;
  }
  if (!isUndefined(filter.max)) {
    return `Preise bis ${filter.max} €`;
  }
  return undefined;
});

const activeFilters = computed(() => {
  const { filter } = fields.value;
  // Categories
  let tags: SelectedTag[] = selectedProductCategories.value.map((category) => ({
    label: category.name as string,
    value: { key: 'productCategoryPaths', value: category.path as string },
  }));

  // Brands, Colors, Compatibilities
  (Object.keys(AGGREGATION_BUCKETS) as Array<keyof typeof AGGREGATION_BUCKETS>).forEach((name) => {
    // We need the category only, when we have cross-category filter
    if (!props.crossCategory && name === 'productCategories') return;
    const { key } = AGGREGATION_BUCKETS[name];
    const values = filter[key];
    if (!values) return;
    const childOptions = options.value[name as AggregationTypes];

    const bucketActiveFilters = Array.from(values as string[])
      .map((value) => {
        if (props.hiddenFilters.includes(name)) return undefined;

        const option = childOptions?.find((o) => o.value === value);
        if (!option) return undefined;
        let { label } = option;
        if (!label) return undefined;
        if (name === 'compatibleProductFamilies') {
          label = `Kompatibel mit ${label}`;
        }
        if (value === 'i22-wifi-compatible') {
          label = 'WLAN-Anbindung';
        }
        return { label, value: { key, value } };
      })
      .filter((item) => item !== undefined)
      .sort((a, b) => (a?.label || '').localeCompare(b?.label || ''));

    tags = tags.concat(bucketActiveFilters);
  });

  // Discount, Prices
  if (filter.discounted) {
    tags.push({
      label: 'Rabattierte Angebote',
      value: { key: 'discounted', value: true },
    });
  }

  if (priceFilterLabel.value && filter.onetimePrice) {
    tags.push({
      label: priceFilterLabel.value,
      value: {
        key: 'onetimePrice',
        value: filter.onetimePrice,
      },
    });
  }

  if (filter.productProviders && !props.hiddenFilters?.includes('providers')) {
    filter.productProviders.forEach((provider) => {
      const mappedProvider = providers.find(({ slug }) => slug === provider)?.name || provider;
      tags.push({
        label: `${providerLabelPrefix} ${mappedProvider}`,
        value: { key: 'productProviders', value: provider },
      });
    });
  }

  if (
    filter.anyInGroupOfProductTagSlugs &&
    !props.hiddenFilters.includes('mza') &&
    !props.hiddenFilters.includes('msh')
  ) {
    filter.anyInGroupOfProductTagSlugs.forEach((group) => {
      if (group.some((tag) => tag.startsWith('mza'))) {
        tags.push({
          label: `Kompatibel mit MagentaZuhause App`,
          value: { key: 'anyInGroupOfProductTagSlugs', value: 'mza' },
        });
      }
      if (group.some((tag) => tag.startsWith('msh'))) {
        tags.push({
          label: `Kompatibel mit Magenta SmartHome App`,
          value: { key: 'anyInGroupOfProductTagSlugs', value: 'msh' },
        });
      }
    });
  }
  return tags;
});

const priceFilterDisabled = computed(() => {
  const { filter } = fields.value;
  return !hasOffers.value && !filter.onetimePrice && !filter.discounted;
});

const isDisplexFullcoverOrPrivacy = computed(
  () =>
    activeFilters.value.some(
      ({ value: { value } }) => value === 'privacy' || value === 'fullcover'
    ) || false
);

const discountedOffersCount = computed(() => {
  if (!props.aggregations) return undefined;
  const discountsAggregation = props.aggregations.discounts;
  if (!discountsAggregation?.discounted) return undefined;
  return discountsAggregation.discounted.offersCount as number;
});
const inSearchContext = computed(() => !!fields.value.search);

const providerFilterDisabled = computed(() => false);

const aggregationTags = computed(() => props.aggregations?.productTags || []);

const wlanTag = computed<OfferProductTagAggregation | undefined>(() =>
  aggregationTags.value.find((item) => item?.tag?.slug === 'i22-wifi-compatible')
);
const mzaCompatibleTag = computed(() =>
  aggregationTags.value.find(
    (item) => item.tag?.slug && ['mzabasic', 'mzapro'].includes(item.tag?.slug || '')
  )
);
const mshCompatibleTag = computed(() =>
  aggregationTags.value.find(
    (item) => item.tag?.slug && ['mshfree', 'mshpro'].includes(item.tag?.slug || '')
  )
);

const isWlanSelected = computed(() => {
  if (!wlanTag.value?.tag?.slug) return false;
  return fields.value?.filter?.productTagSlugs?.includes(wlanTag.value.tag.slug) || false;
});

const isMzaSelected = computed(() => {
  if (!mzaCompatibleTag.value?.tag?.slug) return false;
  return (
    fields.value?.filter?.anyInGroupOfProductTagSlugs?.some((group) =>
      group.includes(mzaCompatibleTag.value?.tag?.slug as string)
    ) || false
  );
});

const isMshSelected = computed(() => {
  if (!mshCompatibleTag.value?.tag?.slug) return false;
  return (
    fields.value?.filter?.anyInGroupOfProductTagSlugs?.some((group) =>
      group.includes(mshCompatibleTag.value?.tag?.slug as string)
    ) || false
  );
});

const showSmarthomeFilter = computed(() => {
  const category = props.variables?.filter?.productCategoryPaths || [];
  if (category) {
    return category.find((c) => c.startsWith('smart-home'));
  }
  return false;
});

const hasRedundantFilterOptions = (type: AggregationTypes | 'categories'): boolean => {
  // A redundant filter option links to the same amount of offers as currently shown
  if (type === 'colors' || type === 'brands') {
    const option = options.value[type];
    if (!Array.isArray(option)) return false;
    return option.every((item) => item.count === props.currentOffersCount);
  }

  if (type === 'categories') {
    return Array.isArray(productCategoryAggregations.value[0]?.children)
      ? productCategoryAggregations.value[0]?.children?.every(
          (category) => category?.offersCount === props.currentOffersCount
        ) || false
      : false;
  }
  return false;
};

const listDropdownDisabled = (listType: AggregationTypes) =>
  !options.value[listType].length ||
  (isDisplexFullcoverOrPrivacy.value && hasRedundantFilterOptions(listType));

const submit = () => {
  const submitFields = cleanFilterVariables(fields.value);
  delete submitFields.pagination;
  emits('submit', submitFields);
};

const updatePrices = (prices: FilterParam) => {
  if (prices.discounted) {
    fields.value.filter.discounted = prices.discounted;
  } else {
    delete fields.value.filter.discounted;
  }
  if (prices.onetimePrice) {
    fields.value.filter.onetimePrice = { ...prices.onetimePrice };
  } else {
    delete fields.value.filter.onetimePrice;
  }
  submit();
};
const updateSort = (sort: FilterQuery['sort']) => {
  fields.value.sort = sort;
  submit();
};
const toggleProvider = (providerSlug: FilterProvider['slug'], newValue: boolean) => {
  const currentProviders = fields.value.filter.productProviders || [];

  fields.value.filter.productProviders = newValue
    ? [...currentProviders, providerSlug]
    : currentProviders.filter((slug) => slug !== providerSlug);
  submit();
};
const toggleWlan = (newValue: boolean) => {
  const currentProductSlugs = fields.value.filter.productTagSlugs || [];

  if (wlanTag.value?.tag?.slug) {
    fields.value.filter.productTagSlugs = newValue
      ? [...currentProductSlugs, wlanTag.value?.tag?.slug]
      : currentProductSlugs.filter((slug) => slug !== wlanTag.value?.tag?.slug);

    submit();
  }
};
const toggleMza = (newValue: boolean) => {
  const currentProductSlugGroups = fields.value.filter.anyInGroupOfProductTagSlugs || [];

  const mzaSlugs = ['mzabasic', 'mzapro'];

  if (mzaCompatibleTag.value?.tag?.slug) {
    fields.value.filter.anyInGroupOfProductTagSlugs = newValue
      ? [...currentProductSlugGroups, mzaSlugs]
      : currentProductSlugGroups.filter(
          (slugGroup) => !slugGroup.some((slug) => mzaSlugs.includes(slug))
        );

    submit();
  }
};
const toggleMsh = (newValue: boolean) => {
  const currentProductSlugGroups = fields.value.filter.anyInGroupOfProductTagSlugs || [];

  const mshSlugs = ['mshfree', 'mshpro'];

  if (mshCompatibleTag.value?.tag?.slug) {
    fields.value.filter.anyInGroupOfProductTagSlugs = newValue
      ? [...currentProductSlugGroups, mshSlugs]
      : currentProductSlugGroups.filter(
          (slugGroup) => !slugGroup.some((slug) => mshSlugs.includes(slug))
        );

    submit();
  }
};

const removeFilter = (selectedTag: SelectedTag['value'] | undefined) => {
  if (!selectedTag) return;
  const { key, value } = selectedTag;
  const { filter } = fields.value;
  switch (key) {
    case 'discounted':
      delete filter.discounted;
      break;
    case 'onetimePrice':
      delete filter.onetimePrice;
      break;
    case 'productProviders':
      filter.productProviders = (filter.productProviders || []).filter(
        (provider) => provider !== value
      );
      break;
    case 'productTagSlugs':
      filter.productTagSlugs = (filter.productTagSlugs || []).filter((slug) => slug !== value);
      break;
    case 'anyInGroupOfProductTagSlugs': {
      filter.anyInGroupOfProductTagSlugs = (filter.anyInGroupOfProductTagSlugs || []).filter(
        (group) => !group.some((item) => item.startsWith(value as string))
      );
      break;
    }
    default: {
      const values = filter[key] || [];
      let newValues = without(values, value);

      if (
        key === 'productCategoryPaths' &&
        newValues.length === 0 &&
        !props.crossCategory &&
        rootProductCategory.value?.path
      ) {
        // When removing all product category filters, slip in the the root
        // category as only category filter instead.
        newValues = [rootProductCategory.value?.path];
      }

      // @ts-ignore
      filter[key] = newValues;
    }
  }
  submit();
};

const clearFilters = () => {
  const filter: FilterParam = {
    onetimePrice: undefined,
    discounted: undefined,
  };

  if (!props.crossCategory) {
    // If no specific product category filters exist, but there is a root category,
    // set the root category as the only category filter
    if (rootProductCategory.value?.path) {
      filter.productCategoryPaths = [rootProductCategory.value.path];
    } else if (
      fields.value.filter?.productCategoryPaths &&
      fields.value.filter.productCategoryPaths.length > 0
    ) {
      // if no root category is present, use any category. Otherwise, the user will be redirected to the start page.
      filter.productCategoryPaths = fields.value.filter.productCategoryPaths;
    }
  }

  fields.value.filter = filter;

  submit();
};

defineExpose({
  activeFilters,
  fields,
  options,
  hasUserFilter,
  priceFilterDisabled,
  priceFilterLabel,
  inSearchContext,
  isDisplexFullcoverOrPrivacy,
  updatePrices,
  updateSort,
  removeFilter,
  clearFilters,
  hasRedundantFilterOptions,
  toggleProvider,
  toggleWlan,
  submit,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.filter-form {
  position: relative;

  &__filter-wrapper {
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 1fr;
    grid-template-areas: 'filters' 'toggles' 'sort';
    padding-top: $grid-base;
    @include mq($mq-md) {
      grid-template-columns: calc(100% / 6) * 5 16.66667%;
      grid-template-areas: 'filters sort' 'toggles toggles';
    }
  }

  &__label {
    display: inline-block;
    font-size: 15px;
    line-height: 18px;
    margin-bottom: 5px;
    width: 100%;
    user-select: none;
    padding-inline: $grid-base * 0.5;
  }

  &__filter-container,
  &__sort-container {
    display: flex;
    flex-flow: row wrap;
    width: 100%;
    align-content: flex-start;
    margin-bottom: $grid-base;
  }

  &__filter-container {
    grid-area: filters;
  }

  &__sort-container {
    grid-area: sort;
  }

  &__toggle-container {
    grid-area: toggles;
    @include mq(false, $mq-md - 1) {
      margin-bottom: $grid-base;
    }

    > * {
      margin-block: $grid-base;
      margin-inline: 0.5 * $grid-base;
    }
  }

  &__entry {
    flex: 0 0 50%;
    max-width: 50%;
    overflow: visible;
    padding-inline: $grid-base * 0.5;
    margin-bottom: $grid-base;

    &-wide {
      flex: 0 0 100%;
      max-width: 100%;
    }

    :deep(.dropdown-container) {
      margin-inline: $grid-base * 0.5;
    }

    @include mq($mq-md) {
      flex: 0 0 20%;
      max-width: 20%;
      margin-bottom: 0;
    }
  }

  &__entry-sort {
    padding-inline: $grid-base * 0.5;
    width: 100%;

    :deep(.dropdown-container) {
      margin-inline: $grid-base * 0.5;
    }
  }

  &__entry-color-preview {
    border-radius: 50%;
    display: block;
    height: 12px;
    width: 12px;
    border: 1px solid var(--color-gray-200);
  }

  &__wlan-toggle {
    &-icon {
      width: 17px;
      height: 17px;
    }

    :deep(.toggle__label) {
      display: flex;
      align-items: center;
      gap: 0.5 * $grid-base;
    }
  }

  &__app-toggle {
    display: inline-flex;

    > * {
      margin-inline: 0.5 * $grid-base;
    }

    &-label {
      @include font-size(15px, 18px);
      grid-row: 1 / 3;
    }

    &-wrapper {
      display: grid;
      grid-template-columns: auto auto;
      gap: $grid-base $grid-base * 0.5;
      align-items: center;

      @include mq($mq-md) {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        vertical-align: top;
        gap: $grid-base;
      }
    }

    &-icon {
      width: 2 * $grid-base;
      height: 2 * $grid-base;
      flex: 0 0 2 * $grid-base;
    }

    :deep(.toggle__label) {
      display: flex;
      align-items: center;
      gap: 0.5 * $grid-base;
    }

    :deep(.toggle) {
      padding-inline: 0;
    }
  }

  &__active {
    padding-inline: $grid-base;
  }
}
</style>
